<template>
  <div v-if="userSettings.developerMode" class="d-flex flex-column h-100">
    <div class="d-none d-md-block px-4 py-5 mx-5 my-5 text-center mel-glass-panel">
      <h1 class="display-5 fw-bold text-primary">Developer</h1>
    </div>

    <div class="px-1 pt-1">
      <div class="container-fluid">
        <div class="card mt-3 mel-glass-panel">
          <div class="card-header">
            <h4>Maintenance Actions</h4>
          </div>
          <div class="card-body">
            <div class="d-grid gap-3">
              <button class="btn btn-primary" @click="resetAllCaches" :disabled="!isAuthenticated">Reset All Caches</button>
              <button class="btn btn-info" @click="signalStaticContentChanged" :disabled="!isAuthenticated">Signal Static Content Changed</button>
              <button class="btn btn-info" @click="signalMasterdataChangedWithTenant" :disabled="!isAuthenticated || !selectedTenantId">Signal Masterdata Changed ({{ selectedTenant?.name }})</button>
              <button class="btn btn-info" @click="signalMasterdataChangedNoTenant" :disabled="!isAuthenticated">Signal Masterdata Changed &lt;all tenants&gt;</button>
              <button v-if="isSystemAdmin" class="btn btn-warning" @click="forceAutoOrder" :disabled="!isAuthenticated || isForcingAutoOrder">
                {{ isForcingAutoOrder ? 'Running…' : 'Force Daily Auto-Order Run' }}
              </button>
              <RouterLink v-if="isSystemAdmin" class="btn btn-outline-primary" :to="TranslationsService.i18nRoute({ name: 'api-request-logs' })">
                Browse API Request Log
              </RouterLink>
            </div>
          </div>
        </div>
      </div>
    </div>

    <FooterComponent />
  </div>
</template>

<script setup lang="ts">
import { ref } from "vue";
import { RouterLink } from "vue-router";
import { storeToRefs } from "pinia";
import { toast } from 'vue3-toastify';
import { useAuthStore, useSessionStore, useUserSettingsStore, useMaintenanceStore } from "@/stores";
import TranslationsService from "@/services/translations.ts";
import FooterComponent from "@/components/FooterComponent.vue";

const authStore = useAuthStore();
const sessionStore = useSessionStore();
const userSettingsStore = useUserSettingsStore();
const maintenanceStore = useMaintenanceStore();

const { isAuthenticated } = storeToRefs(authStore);
const { selectedTenant, selectedTenantId, isSystemAdmin } = storeToRefs(sessionStore);
const { data: userSettings } = storeToRefs(userSettingsStore);

const isForcingAutoOrder = ref(false);

// Action handlers
const resetAllCaches = async () => {
  if (isAuthenticated.value) {
    await maintenanceStore.resetAllCaches();
    toast.success('All caches have been reset', { autoClose: 2000 });
  }
};

const signalStaticContentChanged = async () => {
  if (isAuthenticated.value) {
    await maintenanceStore.signalStaticContentChanged();
    toast.success('Static content changed signal sent', { autoClose: 2000 });
  }
};

const signalMasterdataChangedWithTenant = async () => {
  if (isAuthenticated.value && selectedTenantId.value) {
    await maintenanceStore.signalMasterdataChanged(selectedTenantId.value);
    toast.success(`Masterdata changed signal sent with tenant #${selectedTenantId.value} (${selectedTenant.value?.name})`, { autoClose: 2000 });
  }
};

const signalMasterdataChangedNoTenant = async () => {
  if (isAuthenticated.value) {
    await maintenanceStore.signalMasterdataChanged(null);
    toast.success('Masterdata changed signal sent globally', { autoClose: 2000 });
  }
};

const forceAutoOrder = async () => {
  if (!isAuthenticated.value || isForcingAutoOrder.value) return;
  isForcingAutoOrder.value = true;
  try {
    await maintenanceStore.forceAutoOrder();
    toast.success('Daily auto-order run forced', { autoClose: 2000 });
  } finally {
    isForcingAutoOrder.value = false;
  }
};
</script>

<style scoped>
.mel-glass-panel-content {
  transition: all 0.4s ease 0s;
  max-width: calc(100% - 40px);
  padding: 1rem;
  position: relative;
}
.mel-glass-panel-content.mel-glass-panel-center {
  left: 50%;
  transform: translate(calc(-50% - 20px), 0);
}
</style>
